import React, { useCallback, useRef, useState } from 'react';
import axios from 'axios';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider,
  FormControl, InputLabel, MenuItem, Paper, Select,
  Stack, Switch, Tooltip, Typography,
} from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const TIPO_LABEL = {
  '01': 'Factura', '03': 'Boleta de Venta', '07': 'Nota de Crédito',
  '08': 'Nota de Débito', '09': 'Guía Remitente', '31': 'Guía Transportista',
};

function parsePreview(xmlText) {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlText, 'text/xml');

    const root = doc.documentElement?.tagName || '';
    const isInvoice = root.includes('Invoice');
    const isGuia = root.includes('DespatchAdvice');
    if (!isInvoice && !isGuia) return null;

    const getTag = (tag) => {
      const el = doc.getElementsByTagName(tag)[0];
      return el ? el.textContent.trim() : '';
    };
    const getAttr = (tag, attr) => {
      const el = doc.getElementsByTagName(tag)[0];
      return el ? (el.getAttribute(attr) || '') : '';
    };

    if (isInvoice) {
      const docId = getTag('cbc:ID');
      const tipoCode = getTag('cbc:InvoiceTypeCode');
      const fecha = getTag('cbc:IssueDate');
      const moneda = getTag('cbc:DocumentCurrencyCode');

      const supplierEl = doc.getElementsByTagName('cac:AccountingSupplierParty')[0];
      const customerEl = doc.getElementsByTagName('cac:AccountingCustomerParty')[0];

      const getTagIn = (parent, tag) => {
        if (!parent) return '';
        const el = parent.getElementsByTagName(tag)[0];
        return el ? el.textContent.trim() : '';
      };
      const emisorRuc = supplierEl ? getTagIn(supplierEl, 'cbc:ID') : '';
      const emisorNombre = supplierEl ? getTagIn(supplierEl, 'cbc:RegistrationName') : '';
      const clienteDoc = customerEl ? getTagIn(customerEl, 'cbc:ID') : '';
      const clienteNombre = customerEl ? getTagIn(customerEl, 'cbc:RegistrationName') : '';

      const legalEl = doc.getElementsByTagName('cac:LegalMonetaryTotal')[0];
      const totalPagar = legalEl ? getTagIn(legalEl, 'cbc:PayableAmount') : '';

      return {
        tipo: 'Invoice',
        docId,
        tipoLabel: TIPO_LABEL[tipoCode] || `Tipo ${tipoCode}`,
        tipoCode,
        fecha,
        moneda,
        emisorRuc,
        emisorNombre,
        clienteDoc,
        clienteNombre,
        totalPagar,
      };
    } else {
      const docId = getTag('cbc:ID');
      const tipoCode = getTag('cbc:DespatchAdviceTypeCode');
      const fecha = getTag('cbc:IssueDate');

      const supplierEl = doc.getElementsByTagName('cac:DespatchSupplierParty')[0];
      const customerEl = doc.getElementsByTagName('cac:DeliveryCustomerParty')[0];

      const getTagIn = (parent, tag) => {
        if (!parent) return '';
        const el = parent.getElementsByTagName(tag)[0];
        return el ? el.textContent.trim() : '';
      };
      const emisorRuc = supplierEl ? getTagIn(supplierEl, 'cbc:ID') : '';
      const emisorNombre = supplierEl ? getTagIn(supplierEl, 'cbc:RegistrationName') : '';
      const destinatarioDoc = customerEl ? getTagIn(customerEl, 'cbc:ID') : '';
      const destinatarioNombre = customerEl ? getTagIn(customerEl, 'cbc:RegistrationName') : '';

      return {
        tipo: 'DespatchAdvice',
        docId,
        tipoLabel: TIPO_LABEL[tipoCode] || `Guía Tipo ${tipoCode}`,
        tipoCode,
        fecha,
        moneda: '',
        emisorRuc,
        emisorNombre,
        clienteDoc: destinatarioDoc,
        clienteNombre: destinatarioNombre,
        totalPagar: '',
      };
    }
  } catch {
    return null;
  }
}

export default function ReimprimirXml() {
  const fileInputRef = useRef();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [usarDatosXml, setUsarDatosXml] = useState(false);
  const [formato, setFormato] = useState('A4');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dragging, setDragging] = useState(false);

  const handleFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.xml')) {
      setError('Solo se permiten archivos .xml');
      return;
    }
    setError('');
    setFile(f);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const prev = parsePreview(text);
      setPreview(prev);
      if (!prev) setError('El XML no parece ser un comprobante UBL 2.1 SUNAT válido.');
    };
    reader.readAsText(f, 'utf-8');
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    handleFile(f);
  }, []);

  const handleDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const handleDragLeave = () => setDragging(false);

  const handleGenerar = async () => {
    if (!file) return;
    setLoading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('xmlFile', file);
      formData.append('formato', formato);
      formData.append('usarDatosXml', String(usarDatosXml));

      const r = await axios.post('/api/xml-reprint/pdf', formData, {
        responseType: 'blob',
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (e.response?.data) {
        try {
          const text = await e.response.data.text();
          const parsed = JSON.parse(text);
          setError(parsed.error || 'Error generando PDF.');
        } catch {
          setError('Error generando PDF.');
        }
      } else {
        setError(e.message || 'Error generando PDF.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLimpiar = () => {
    setFile(null);
    setPreview(null);
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', p: { xs: 1, md: 2 } }}>
      <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
        <ArticleIcon sx={{ color: '#1a237e', fontSize: 30 }} />
        <Box>
          <Typography variant="h6" fontWeight="bold" color="#1a237e" lineHeight={1.2}>
            Reimprimir XML
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Convierte un XML UBL 2.1 SUNAT en PDF usando el formato del sistema
          </Typography>
        </Box>
      </Stack>

      {/* Drop zone */}
      <Paper
        variant="outlined"
        onClick={() => fileInputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        sx={{
          border: '2px dashed',
          borderColor: dragging ? 'primary.main' : file ? 'success.main' : 'divider',
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          cursor: 'pointer',
          bgcolor: dragging ? 'primary.50' : file ? 'success.50' : 'grey.50',
          transition: 'all 0.2s',
          mb: 2,
          '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50' },
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".xml"
          style={{ display: 'none' }}
          onChange={(e) => handleFile(e.target.files[0])}
        />
        <CloudUploadIcon sx={{ fontSize: 48, color: file ? 'success.main' : 'action.disabled', mb: 1 }} />
        {file ? (
          <>
            <Typography fontWeight="bold" color="success.main">{file.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {(file.size / 1024).toFixed(1)} KB — clic para cambiar
            </Typography>
          </>
        ) : (
          <>
            <Typography fontWeight="bold" color="text.secondary">
              Arrastra el XML aquí o haz clic para seleccionar
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Soporta Factura, Boleta, Nota Crédito/Débito y Guía de Remisión (UBL 2.1)
            </Typography>
          </>
        )}
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Preview */}
      {preview && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
            <Chip
              label={preview.tipoLabel}
              size="small"
              sx={{ bgcolor: '#e8f0fe', color: '#1a237e', fontWeight: 'bold' }}
            />
            <Typography fontWeight="bold" fontSize={15}>{preview.docId}</Typography>
            <Typography variant="caption" color="text.secondary">{preview.fecha}</Typography>
            {preview.moneda && (
              <Chip label={preview.moneda} size="small" variant="outlined" />
            )}
          </Stack>
          <Divider sx={{ mb: 1.5 }} />
          <Stack spacing={0.5}>
            <Stack direction="row" spacing={1}>
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90 }}>Emisor:</Typography>
              <Typography variant="caption" fontWeight="bold">{preview.emisorRuc} — {preview.emisorNombre}</Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90 }}>
                {preview.tipo === 'DespatchAdvice' ? 'Destinatario:' : 'Cliente:'}
              </Typography>
              <Typography variant="caption" fontWeight="bold">{preview.clienteDoc} — {preview.clienteNombre}</Typography>
            </Stack>
            {preview.totalPagar && (
              <Stack direction="row" spacing={1}>
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90 }}>Total:</Typography>
                <Typography variant="caption" fontWeight="bold" color="success.dark">
                  {preview.moneda || 'PEN'} {parseFloat(preview.totalPagar).toFixed(2)}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Paper>
      )}

      {/* Options */}
      {preview && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Stack spacing={2}>
            {/* Datos empresa */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                <Typography fontWeight="bold" fontSize={14}>Datos del emisor en el PDF</Typography>
                <Tooltip title="'Datos del XML' usa el RUC y razón social del emisor declarado dentro del archivo XML. 'Datos del sistema' usa los datos de empresa configurados en este sistema.">
                  <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Typography
                  variant="body2"
                  fontWeight={!usarDatosXml ? 'bold' : 'normal'}
                  color={!usarDatosXml ? 'primary.main' : 'text.secondary'}
                >
                  Datos del sistema
                </Typography>
                <Switch
                  checked={usarDatosXml}
                  onChange={(e) => setUsarDatosXml(e.target.checked)}
                  color="success"
                  size="small"
                />
                <Typography
                  variant="body2"
                  fontWeight={usarDatosXml ? 'bold' : 'normal'}
                  color={usarDatosXml ? 'success.dark' : 'text.secondary'}
                >
                  Datos del XML
                </Typography>
              </Stack>
              {usarDatosXml && (
                <Alert severity="info" sx={{ mt: 1, py: 0.5 }} icon={false}>
                  <Typography variant="caption">
                    El PDF mostrará: <b>{preview.emisorRuc} — {preview.emisorNombre}</b>
                  </Typography>
                </Alert>
              )}
            </Box>

            <Divider />

            {/* Formato */}
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel>Formato de papel</InputLabel>
              <Select
                value={formato}
                onChange={(e) => setFormato(e.target.value)}
                label="Formato de papel"
              >
                <MenuItem value="A4">A4 (hoja completa)</MenuItem>
                <MenuItem value="A5">A5 (media hoja)</MenuItem>
                <MenuItem value="T80">T80 (ticket 80mm)</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Paper>
      )}

      {/* Acciones */}
      <Stack direction="row" spacing={1.5} justifyContent="flex-end">
        {file && (
          <Button variant="outlined" color="inherit" onClick={handleLimpiar} disabled={loading}>
            Limpiar
          </Button>
        )}
        <Button
          variant="contained"
          color="primary"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdfIcon />}
          disabled={!file || !preview || loading}
          onClick={handleGenerar}
        >
          {loading ? 'Generando...' : 'Generar PDF'}
        </Button>
      </Stack>
    </Box>
  );
}
